import React, { useEffect,useMemo, useRef, useState } from "react";
import {saveWellnessAssessment,getWellnessAssessments} from "../../service/wellnessAssessment";
import {
    Box,
    Typography,
    Paper,
    Button,
    IconButton,
    Grid,
    Card,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import {
    ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { drains } from "./AssessmentFlow";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { getSmartBiometricSound } from "../../service/home";
import soundTherapyTracks from "./SmartBiometricTherapy.json";
import * as Icons from "@mui/icons-material";

const ResultPage = () => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const [selectedTrack, setSelectedTrack] = useState(null);
    const [audioUrl, setAudioUrl] = useState("");
    const [openAudioDialog, setOpenAudioDialog] = useState(false);
    const [loading, setLoading] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isAudioCompleted, setIsAudioCompleted] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false); // true only while audio is actually playing

    const handleManualPlay = () => {
        if (!audioRef.current) return;
        audioRef.current.play().catch((err) => console.log("Manual play failed", err));
    };
   const {bodyData = {}, battery = 50, selectedDrains = [], heartRate = null} = state || {};
   
    const audioRef = useRef(null);
    const user = JSON.parse(localStorage.getItem("user"));

    
const bodyInfo = {
    "Head": { emoji: "🧠", desc: "Tension headaches — cognitive overload, worry, anxiety" },
    "Neck": { emoji: "🦴", desc: "Cervical tension — mental strain, screen fatigue, vigilance" },
    "Chest": { emoji: "❤️", desc: "Chest tightness — anxiety, grief, emotional suppression" },
    "Stomach": { emoji: "🫁", desc: "Gut tension — anxiety, fear, emotional processing (gut-brain axis)" },
    "Left Shoulder": { emoji: "💪", desc: "Carried burden — emotional weight, responsibility" },
    "Right Shoulder": { emoji: "💪", desc: "Carried burden — overwork, accumulated stress" },
    "Left Arm": { emoji: "🦾", desc: "Held tension — control needs, frustration" },
    "Right Arm": { emoji: "🦾", desc: "Held tension — action suppression, overuse" },
    "Left Hand": { emoji: "🤲", desc: "Grip tension — holding on, anxiety, control" },
    "Right Hand": { emoji: "🤲", desc: "Grip tension — perfectionism, repetitive strain" },
    "Lower Back": { emoji: "🔻", desc: "Foundation strain — financial stress, feeling unsupported" },
    "Left Leg": { emoji: "🦵", desc: "Restlessness — fight-or-flight activation" },
    "Right Leg": { emoji: "🦵", desc: "Restlessness — urge to run, grounding loss" },
    "Left Foot": { emoji: "🦶", desc: "Grounding loss — disconnection, instability" },
    "Right Foot": { emoji: "🦶", desc: "Grounding loss — restlessness, instability" },
};


    // 🔧 FORMAT KEY FIX

    const formatPart = (part) => {
        if (part === "lowBack") return "Lower Back";

        return part
            .replace(/([A-Z])/g, " $1")
            .replace(/^./, (str) => str.toUpperCase());
    };

  
  const tensionEntries = Object.entries(bodyData).filter(
        ([, v]) => v > 0
    );

    

   const filteredDrains = drains.filter(d =>
    selectedDrains.includes(d.id)
);

const totalPossibleStress = 15;

const currentStress = tensionEntries.reduce((sum, [, level]) => {
    return sum + level;
}, 0);

const stressRatio = currentStress / totalPossibleStress;

const tensionScore = Math.round(
    Math.max(5, Math.min(95, 100 - (stressRatio * 120)))
);

const drainPenalty = filteredDrains.length * 4;

const energyScore = Math.max(
    5,
    Math.min(95, battery - drainPenalty)
);

const wellnessScore = Math.round(
    (tensionScore * 0.5) + (energyScore * 0.5)
);
    const getLevel = (level) => {
        if (level === 1) return { text: "MILD", color: "#14b8a6" };
        if (level === 2) return { text: "MODERATE", color: "#0ea5a4" };
        return { text: "INTENSE", color: "#7c3aed" };
    };

    const getStatusTitle = () => {
        if (wellnessScore >= 70) return "Well Balanced";
        if (wellnessScore >= 45) return "Moderate Stress Load";
        return "Elevated Stress Response";
    };

    const getStatusText = () => {
        if (wellnessScore >= 70)
            return "Your body and energy are both in good shape. Minimal tension and adequate reserves suggest a well-managed stress response. Maintenance therapy can help sustain this.";
        if (wellnessScore >= 45)
            return "Your body is holding tension and/or your energy reserves are under pressure. Targeted sound therapy can address both the physical patterns and mental depletion.";
return "Significant body tension combined with depleted energy indicates your system is under considerable strain. Prioritize therapeutic recovery — your body and mind are both asking for care.";    };

    const getCategory = (wellnessScore) => {
        if (wellnessScore >= 70) return "low";
        if (wellnessScore >= 45) return "medium";
        return "high";
    };

    const getRandomSongs = (arr, count = 3) => {
        if (!arr || arr.length === 0) return [];

        // shuffle copy (Fisher-Yates)
        const shuffled = [...arr];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }

        return shuffled.slice(0, Math.min(count, shuffled.length));
    };

    // usage
    const category = getCategory(wellnessScore);

//     const songs = useMemo(() => {
//     return getRandomSongs(soundTherapyTracks[category], 3);
// }, [category]);



const songs = useMemo(() => {
    const selectedSongs = getRandomSongs(soundTherapyTracks[category], 3);

    if (user?.account_id !== 5) {
        return selectedSongs;
    }

    return selectedSongs.map(song => ({
        ...song,
        trackId: `consero-${song.trackId}`,
    }));
}, [category, user?.account_id]);

const payload = {
    user_id: user?.id,

    wellness_score: wellnessScore,
    tension_score: tensionScore,
    energy_score: energyScore,
    battery_input: battery,

    wellness_status: getStatusTitle(),

    tension_map: tensionEntries.map(([part, level]) => ({
        part: formatPart(part),
        raw_key: part,
        level: level,
        label: getLevel(level).text
    })),

    energy_drains: filteredDrains.map(item => ({
        id: item.id,
        label: item.label,
        insight: item.insight
    })),

    recommended_tracks: songs.map(song => ({
        track_id: song.trackId,
        title: song.title,
        subtitle: song.subtitle1
    }))
};

useEffect(() => {

    if (!user?.id) return;

    const saveAssessment = async () => {

        try {

            await saveWellnessAssessment(payload);

            console.log("Assessment saved successfully");

        } catch (error) {

            console.error("Failed to save assessment", error);
        }
    };

    saveAssessment();

}, [user?.id]);

    const formatTime = (seconds = 0) => {
        const m = Math.floor(seconds / 60);
        const s = Math.floor(seconds % 60);
        return `${m}:${s.toString().padStart(2, "0")}`;
    };

    return (
        <Box sx={{
            background: "#f3f4f6",
            minHeight: {
                xs: 'calc(100vh - 56px)',
                sm: 'calc(100vh - 64px)',
                md: 'calc(100vh - 70px)',
            },
            overflow: 'hidden',
            width: '100%',
        }}>
            <Box
                sx={{
                    // py: 4,
                    background: "#f3f4f6",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    px: 2,
                    position: "relative",
                    width: '100%',
                    maxWidth: '100%',
                }}
            >
                <Box display="flex" justifyContent="space-between" gap={1} mb={2} width="100%">
                    <Box sx={{ mt: 2, }}>
                        <Typography variant="h6" sx={{
                            fontWeight: 700, background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                        }}>
                            Wellness Assessment
                        </Typography>
                    </Box>

                    <IconButton
                        onClick={() => navigate("/echo-sounds")}
                        sx={{
                            backgroundColor: '#F0F5FF',
                            width: 35,
                            height: 35,
                            mt: 2,
                            borderRadius: '50%',
                            color: '#0B2D6B',
                            position: "absolute",
                            top: 0,
                            right: 10,
                            '&:hover': {
                                backgroundColor: '#E0EBFF'
                            }
                        }}
                    >
                        <ArrowBackIcon />
                    </IconButton>
                </Box>

                {/* TITLE */}
                <Typography textAlign="center" fontSize={24} fontWeight="bold">
                    Your Wellness Profile
                </Typography>

                <Typography textAlign="center" color="#6b7280" mb={3}>
                    Body tension + energy drain analysis
                </Typography>

               
                <Paper sx={{ p: 3, borderRadius: "20px", mb: 3, width: { xs: '100%', sm: '90%', md: 460 }, maxWidth: 460 }}>
                    <Box display="flex" gap={2} alignItems="center">

                        <Box position="relative" width={100} height={100}>
                            <svg width="100" height="100">
                                <circle cx="50" cy="50" r="40" stroke="#e5e7eb" strokeWidth="8" fill="none" />
                                <circle
                                    cx="50"
                                    cy="50"
                                    r="40"
                                    stroke="#7c3aed"
                                    strokeWidth="8"
                                    fill="none"
                                    strokeDasharray={2 * Math.PI * 40}
                                    strokeDashoffset={
                                        2 * Math.PI * 40 - (wellnessScore / 100) * 2 * Math.PI * 40
                                    }
                                    strokeLinecap="round"
                                    transform="rotate(-90 50 50)"
                                />
                            </svg>

                            <Box sx={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                textAlign: "center",
                            }}>
                                <Typography fontWeight="bold">{wellnessScore}</Typography>
                                <Typography fontSize={10} color="#6b7280">WELLNESS</Typography>
                            </Box>
                        </Box>

                        <Box>
                            <Typography fontWeight="bold">{getStatusTitle()}</Typography>
                            <Typography fontSize={13} color="#6b7280">
                                {getStatusText()}
                            </Typography>
                        </Box>
                    </Box>
                </Paper>

                {/* ================= BODY + BATTERY ================= */}
                <Paper sx={{ p: 3, borderRadius: "20px", mb: 3, width: { xs: '100%', sm: '90%', md: 460 }, maxWidth: 460, gap: 2, display: "flex" }}>

                    {/* BODY */}
                    <Paper sx={{ flex: 1, p: 2, borderRadius: "16px", textAlign: "center", background: "#f1f5f9" }}>
                        <Box position="relative" width={90} height={90} mx="auto">
                            <svg width="90" height="90">
                                <circle cx="45" cy="45" r="34" stroke="#e5e7eb" strokeWidth="7" fill="none" />
                                <circle
                                    cx="45"
                                    cy="45"
                                    r="34"
                                    stroke="#14b8a6"
                                    strokeWidth="7"
                                    fill="none"
                                    strokeDasharray={2 * Math.PI * 34}
                                    strokeDashoffset={
                                        2 * Math.PI * 34 - (tensionScore / 100) * 2 * Math.PI * 34
                                    }
                                    transform="rotate(-90 45 45)"
                                />
                            </svg>

                            <Typography sx={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                fontWeight: "bold",
                                color: "#14b8a6",
                            }}>
                                {tensionScore}
                            </Typography>
                        </Box>

                        <Typography fontSize={12} color="#6b7280" mt={1}>
                            BODY SCAN
                        </Typography>
                    </Paper>

                    {/* BATTERY */}
                    <Paper sx={{ flex: 1, p: 2, borderRadius: "16px", textAlign: "center", background: "#f1f5f9" }}>
                        <Box position="relative" width={90} height={90} mx="auto">
                            <svg width="90" height="90">
                                <circle cx="45" cy="45" r="34" stroke="#e5e7eb" strokeWidth="7" fill="none" />
                                <circle
                                    cx="45"
                                    cy="45"
                                    r="34"
                                    stroke="#7c3aed"
                                    strokeWidth="7"
                                    fill="none"
                                    strokeDasharray={2 * Math.PI * 34}
                                    strokeDashoffset={
                                        2 * Math.PI * 34 - (energyScore / 100) * 2 * Math.PI * 34
                                    }
                                    transform="rotate(-90 45 45)"
                                />
                            </svg>

                            <Typography sx={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                fontWeight: "bold",
                                color: "#7c3aed",
                            }}>
                               {energyScore}
                            </Typography>
                        </Box>

                        <Typography fontSize={12} color="#6b7280" mt={1}>
                            ENERGY BATTERY
                        </Typography>
                    </Paper>
                </Paper>

                {/* ================= TENSION MAP ================= */}
                <Paper sx={{ p: 3, borderRadius: "20px", mb: 1.5, width: { xs: '100%', sm: '90%', md: 460 }, maxWidth: 460, gap: 2, display: "flex", flexDirection: "column" }}>
                    <Typography fontWeight="bold">Tension Map</Typography>

                    {tensionEntries.map(([part, level]) => {
                        const { text, color } = getLevel(level);
                        const formatted = formatPart(part);

                        return (
                            <Box
                                key={part}
                                sx={{
                                    mt: 1,
                                    p: 2,
                                    borderRadius: "14px",
                                    background: "#f1f5f9",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    borderLeft: `4px solid ${color}`,
                                }}
                            >
                                <Box display="flex" alignItems="center" gap={1.5}>
                                    <Box fontSize={20}>
                                        {bodyInfo[formatted]?.emoji || "⚡"}
                                    </Box>

                                    <Box>
                                        <Typography fontWeight="bold">
                                            {formatted}
                                        </Typography>

                                        <Typography fontSize={12} color="#6b7280">
                                            {bodyInfo[formatted]?.desc}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box
                                    sx={{
                                        px: 2,
                                        py: 0.5,
                                        borderRadius: "999px",
                                        background: `${color}20`,
                                        color: color,
                                        fontSize: 12,
                                        fontWeight: "bold",
                                    }}
                                >
                                    {text}
                                </Box>
                            </Box>
                        );
                    })}
                </Paper>

                {/* ================= ENERGY DRAINS ================= */}
                <Paper sx={{ p: 3, borderRadius: "20px", mb: 3, width: { xs: '100%', sm: '90%', md: 460 }, maxWidth: 460, gap: 2, display: "flex", flexDirection: "column" }}>
                    <Typography fontWeight="bold">Energy Drains</Typography>

                    {filteredDrains.map((item) => {
                        return (
                            <Box
                                key={item.id}
                                sx={{
                                    mt: 1,
                                    p: 2,
                                    borderRadius: "14px",
                                    background: "#f1f5f9",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    borderLeft: "4px solid #6366f1",
                                }}
                            >
                                {/* LEFT */}
                                <Box display="flex" alignItems="center" gap={1.5}>
                                    <Box fontSize={20}>
                                        {item.icon || "⚡"}
                                    </Box>

                                    <Box>
                                        <Typography fontWeight="bold">
                                            {item.label}
                                        </Typography>

                                        <Typography fontSize={12} color="#6b7280">
                                            {item.insight}
                                        </Typography>
                                    </Box>
                                </Box>

                              
                              
                            </Box>
                        );
                    })}
                </Paper>

            
                <Paper sx={{ p: 3, borderRadius: 3, mb: 2, width: { xs: '100%', sm: '90%', md: 460 }, maxWidth: 460, }}>
                    <Typography fontWeight={800}>
                        Recommended Therapeutic Sounds
                    </Typography>

                    <Typography fontSize={12} color="#6b7280" mb={2}>
                        Frequency-matched healing audio for your state
                    </Typography>

                    <Box display="flex" flexDirection="column" gap={1.5}>
                       {songs.map((s, i) => (
                            <Box
                                key={i}
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    p: 2,
                                    borderRadius: "16px",
                                    background: "#f4f6f8"
                                }}
                            >
                                {/* LEFT */}
                                <Box display="flex" gap={2} alignItems="center">

                                    <Box sx={{
                                        width: 42,
                                        height: 42,
                                        borderRadius: 2,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        background: "#e0f2fe",
                                    }}>
                                        {(() => {
                                            const IconComponent = Icons[s.icon] || Icons.MusicNote;
                                            return <IconComponent sx={{ color: "#0284c7" }} />;
                                        })()}
                                    </Box>

                                    <Box>
                                        <Typography fontWeight="bold">
                                            {s.title}
                                        </Typography>

                                        <Typography fontSize={12} color="text.secondary">
                                            {s.subtitle1}
                                        </Typography>

                                        <Box fontSize={12} color="text.secondary">
                                            {s.subtitle2}
                                        </Box>

                                       {/* <Box display="flex" gap={1} mt={1}>
                                          <Chip label={`${Math.round(s.duration / 60)} min`} size="small" />
                                      </Box> */}
                                    </Box>
                                </Box>

                                {/* PLAY */}
                                <IconButton
                                    sx={{ background: "#e0f2fe" }}
                                    onClick={async () => {
                                        try {
                                            setLoading(true);

                                            setSelectedTrack(s);
                                            setIsAudioCompleted(false);
                                            setIsPlaying(false);
                                            setCurrentTime(0);
                                            setDuration(0);

                                            const data = await getSmartBiometricSound(s.trackId);

                                            setAudioUrl(data.track_url);

                                            setOpenAudioDialog(true);

                                        } catch (err) {
                                            console.error(err);
                                        } finally {
                                            setLoading(false);
                                        }
                                    }}
                                >
                                    <PlayArrowIcon sx={{ color: "#0284c7" }} />
                                </IconButton>
                            </Box>
                        ))}
                    </Box>
                </Paper>

                {/* BUTTON */}
                <Button
                    fullWidth
                    sx={{
                        py: 1.5,
                        borderRadius: "14px",
                        border: "2px solid #0ea5a4",
                        color: "#0ea5a4",
                        width: { xs: '100%', sm: '90%', md: 460 },
                        maxWidth: 460,
                    }}
                    onClick={() => navigate("/decode-stress")}
                >
                    ⟳ Start Over
                </Button>

                <Dialog
                    open={openAudioDialog}
                    onClose={() => setOpenAudioDialog(false)}
                    fullWidth
                    maxWidth="md"
                >
                    <DialogTitle sx={{ fontWeight: "bold", color: "#7843f3" }}>
                        Wellness Assessment
                    </DialogTitle>

                    <DialogContent dividers>
                        <Box className="audio-player">

                            {/* 👇 YOUR EXISTING CODE STARTS HERE */}

                            <Box className="player-header" sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                <Box
                                    sx={{
                                        width: 60,
                                        height: 60,
                                        borderRadius: 2,
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 28,
                                        color: 'white',
                                    }}
                                >
                                    🎵
                                </Box>

                                <Box>
                                    <Typography variant="h6" sx={{ color: "#7843f3" }}>
                                        {selectedTrack?.title}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        echo Therapeutic Session
                                    </Typography>
                                </Box>
                            </Box>

                            <Button
                                variant="text"
                                size="small"
                                onClick={() => setOpenAudioDialog(false)}
                                sx={{
                                    position: "absolute",
                                    right: 12,
                                    top: 12,
                                    color: "#7843f3",
                                    fontWeight: 600,
                                }}
                            >
                                Back
                            </Button>

                            {/* AUDIO */}
                            <audio
                                ref={audioRef}
                                src={audioUrl}
                                autoPlay
                                preload="auto"
                                style={{ display: "none" }}

                                onPlay={() => setIsPlaying(true)}

                                onPause={() => setIsPlaying(false)}

                                onSeeking={(e) => {
                                    e.target.currentTime = currentTime;
                                }}

                                onLoadedMetadata={(e) => {
                                    setDuration(e.target.duration);
                                }}

                                onTimeUpdate={(e) => {
                                    setCurrentTime(e.target.currentTime);
                                }}

                                onEnded={() => setIsAudioCompleted(true)}
                            />

                            {/* TAP TO PLAY (mobile autoplay fallback) */}
                            {!isPlaying && !isAudioCompleted && (
                                <Button
                                    variant="contained"
                                    fullWidth
                                    sx={{
                                        mt: 2,
                                        py: 1.4,
                                        borderRadius: 2,
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
                                    }}
                                    onClick={handleManualPlay}
                                >
                                    {currentTime > 0 ? '▶ Resume' : '▶ Tap to Play'}
                                </Button>
                            )}

                            {/* PROGRESS */}
                            <Box sx={{ mt: 2 }}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                                    <Typography variant="caption">{formatTime(currentTime)}</Typography>
                                    <Typography variant="caption">{formatTime(duration)}</Typography>
                                </Box>

                                <Box sx={{ height: 8, borderRadius: 4, backgroundColor: "#e5e7eb", overflow: "hidden" }}>
                                    <Box
                                        sx={{
                                            height: "100%",
                                            width: `${(currentTime / duration) * 100 || 0}%`,
                                            background: "linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)",
                                            transition: "width 0.25s linear",
                                        }}
                                    />
                                </Box>
                            </Box>

                            {/* COMPLETE BUTTON */}
                            {isAudioCompleted && (
                                <Button
                                    variant="contained"
                                    sx={{
                                        mt: 3,
                                        mx: 'auto',
                                        display: 'block',
                                        background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
                                    }}
                                    onClick={() => setOpenCompleteDialog(true)}
                                >
                                    Complete Session
                                </Button>
                            )}

                            {/* 👆 YOUR EXISTING CODE ENDS HERE */}

                        </Box>
                    </DialogContent>
                </Dialog>

            </Box>
        </Box >
    );
};

export default ResultPage;  
